import React, { FC, useEffect, useState } from 'react';
import Api from "../../../../api";
import styles from "./components.module.scss";
import MaterialInput from "components/Inputs/MaterialInput";
import CloseIcon from '@material-ui/icons/Close';
import { SimpleModal } from "components/Modal";
import { RemoveContactInfoModal } from "../../../landing/SignUp2_Talent/components/RemoveContactinfoModal";
import { ChooseBio } from "../../../landing/SignUp2_Talent/components/ChooseBio";
import { CircularProgress } from "@mui/material";
import { WritingAssistFlow } from "components/WritingAssist";
import { isWritingAssistV2Enabled } from "utils/writingAssistV2";

export interface AboutMeProps {
    about?: string,
    name?: string,
    isOpen: boolean,
    onClose: () => void,
    onSaveAbout: (about: string) => void
}

interface CheckTextResponse {
    spellChecked: string;
    enhanced: string;
    error?: boolean;
    message?: string;
}

const countWords = (inputString: string): number => {
    const words: string[] = inputString.trim().split(/\s+/);
    return words.length;
}

const tryLaterText = "Sorry, you've used all of your profile assists for today. Please skip or try again tomorrow."

const LegacyAboutMe: FC<AboutMeProps> = ({
    about,
    name,
    isOpen,
    onClose,
    onSaveAbout
}) => {
    const [inputValue, setInputValue] = useState(about || '');
    const [spellCheckedResponse, setSpellCheckedResponse] = useState('');
    const [enhancedResponse, setEnhancedResponse] = useState('');
    const [removeContactInfoModal, setRemoveContactInfoModal] = useState(false);
    const [isChooseBioOpen, setIsChooseBioOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [aboutError, setAboutError] = useState<string>();
    const [rewritesLeft, setRewritesLeft] = useState<number>(5);
    const [rewritesLeftError, setRewritesLeftError] = useState<string>("");

    const getRewritesLeft = async () => {
        try {
            const response = await Api.getAiAssistsLeft();
            if (response.aiAssistsLeft && typeof (response.aiAssistsLeft === 'number')) {
                setRewritesLeft(response.aiAssistsLeft);
                return response.aiAssistsLeft;
            } else {
                console.error('Invalid response format:', response);
                setRewritesLeftError(tryLaterText);
                setRewritesLeft(0);
                return 0;
            }
        } catch (error) {
            console.error('Error fetching rewrites left:', error);
            setRewritesLeftError(tryLaterText);
            setRewritesLeft(0);
            return 0;
        }
    };

    const onSubmit = async (event?: React.FormEvent) => {
        event?.preventDefault();
        getRewritesLeft();
        if (rewritesLeft > 0) {
            if (countWords(inputValue) < 3) {
                setAboutError('Tell us how you shine!');
                return;
            }
            setAboutError(undefined);
            try {
                setLoading(true);
                const message: CheckTextResponse = await Api.checkTextContactInfo(inputValue);
                if (message?.error) {
                    setAboutError(message?.message || 'An error occurred. Please try again later.');
                } else {
                    setSpellCheckedResponse(message.spellChecked);
                    setEnhancedResponse(message.enhanced);
                    setIsChooseBioOpen(true);
                    setRewritesLeft(rewritesLeft - 1);
                }
            } catch (e) {
                console.error('There was an error!', e);
                setAboutError('An unexpected error occurred. Please try again later.');
            } finally {
                setLoading(false);
            }
        } else {
            setRewritesLeft(0);
            setRewritesLeftError(tryLaterText);
            console.error(rewritesLeftError);
        }
    };

    useEffect(() => {
        setInputValue(about || '');
    }, [about]);

    useEffect(() => {
        getRewritesLeft();
    }, []);

    const handleInputChange = (event: any) => {
        setInputValue(event.target.value);
    }

    if (!isOpen) return null;

    // If ChooseBio is open, render that instead
    if (isChooseBioOpen) {
        return (
            <ChooseBio
                isOpen={isChooseBioOpen}
                onBack={() => setIsChooseBioOpen(false)}
                spellCheckedResponse={spellCheckedResponse}
                enhancedResponse={enhancedResponse}
                onSave={(about: string) => {
                    onSaveAbout(about);
                    onClose();
                }}
                removeContactInfoModal={removeContactInfoModal}
                setRemoveContactInfoModal={setRemoveContactInfoModal}
                rewritesLeft={rewritesLeft}
            />
        );
    }

    return (
        <>
            {/* Main Modal */}
            <div className={styles.wizardModalOverlay} onClick={onClose}>
                <div className={styles.wizardModalContainer} onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className={styles.wizardModalHeader}>
                        <div className={styles.wizardHeaderContent}>
                            <h1 className={styles.wizardModalTitle}>About You</h1>
                            <p className={styles.wizardModalSubtitle}>
                                Hey {name}! Tell offices how you shine...
                            </p>
                        </div>
                        <button className={styles.wizardCloseButton} onClick={onClose} aria-label="Close modal">
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Body */}
                    <form className={styles.wizardModalBody} onSubmit={onSubmit}>
                        <div className={styles.wizardInputContainer}>
                            <MaterialInput
                                label={'About You'}
                                defaultValue={"What are your passions in dentistry? Personal interests?..."}
                                id={'about'}
                                name={"about"}
                                type={"text"}
                                value={inputValue}
                                onChange={handleInputChange}
                                multiline={true}
                                error={!!aboutError}
                                helperText={aboutError}
                                rows={7}
                            />
                        </div>

                        {rewritesLeftError && (
                            <div className={styles.wizardErrorMessage}>
                                {rewritesLeftError}
                            </div>
                        )}

                        <div className={styles.wizardInfoBox}>
                            <h4>Need help writing?</h4>
                            <p>Don't worry, we've got you. When you hit "Improve Bio" we'll magically fix any spelling & punctuation, and you'll even see an optional version with some flair and personality.</p>
                            <p><strong>Note:</strong> Don't include contact information (email, phone, etc.) in this section, it gets removed automatically.</p>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className={styles.wizardModalFooter}>
                        <button
                            type="button"
                            className={styles.wizardCancelButton}
                            onClick={onClose}
                        >
                            Skip for Now
                        </button>
                        <button
                            type="submit"
                            className={styles.wizardSaveButton}
                            onClick={onSubmit}
                            disabled={loading || countWords(inputValue) < 3}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Improve Bio'}
                        </button>
                    </div>

                    {/* Loader Overlay */}
                    {loading && (
                        <div className={styles.wizardLoaderOverlay}>
                            <CircularProgress />
                        </div>
                    )}
                </div>
            </div>

            {/* Remove Contact Info Modal */}
            <SimpleModal isOpen={removeContactInfoModal}>
                <RemoveContactInfoModal onCloseButtonClick={() => setRemoveContactInfoModal(false)} />
            </SimpleModal>
        </>
    );
}

const WritingAssistAboutMe: FC<AboutMeProps> = ({
    about,
    name,
    isOpen,
    onClose,
    onSaveAbout,
}) => {
    if (!isOpen) return null;

    return (
        <div className={styles.wizardModalOverlay} onClick={onClose} data-writing-assist-version="v2">
            <div className={styles.wizardModalContainer} onClick={(event) => event.stopPropagation()}>
                <div className={styles.wizardModalHeader}>
                    <div className={styles.wizardHeaderContent}>
                        <h1 className={styles.wizardModalTitle}>About You</h1>
                    </div>
                    <button className={styles.wizardCloseButton} onClick={onClose} aria-label="Close modal">
                        <CloseIcon />
                    </button>
                </div>
                <div className={styles.wizardModalBody}>
                    <WritingAssistFlow
                        context="aboutMe"
                        firstName={name || ''}
                        initialText={about || ''}
                        save={async ({ text }) => {
                            const result = await Api.updateHygienistAccount({ about: text });
                            onSaveAbout(text);
                            return result;
                        }}
                        onSaved={onClose}
                    />
                </div>
            </div>
        </div>
    );
};

export const AboutMe: FC<AboutMeProps> = (props) => (
    isWritingAssistV2Enabled()
        ? <WritingAssistAboutMe {...props} />
        : <LegacyAboutMe {...props} />
);
